import React from 'react';
import { useLocation } from 'react-router-dom';
import { Search, X, ChevronLeft } from 'lucide-react';
import { useApp } from '../appContext';
import { PeterMark } from './PeterAvatar';
import {
  CATEGORIES, HELP_TOPICS, visibleTopics, topicForPath, searchTopics, topicById, orderTopics,
} from './helpContent';

// Same flags as App.js: help for a switched-off feature would describe a page nobody can open.
const FLAGS = {
  resources: process.env.REACT_APP_RESOURCES_ENABLED === 'true',
  scheduling: process.env.REACT_APP_SCHEDULING_ENABLED === 'true',
};

// Peter falls back to the static footer note when unset, so the drawer is unchanged wherever the
// API has no AI credential configured.
const PETER_ENABLED = process.env.REACT_APP_PETER_ENABLED === 'true';

// ---------- markdown-lite ----------
// Handles exactly what helpContent.js's bodies use: `## heading`, `- bullet`, blank-line paragraphs,
// **bold**, *italic* and `code`. A markdown library would be a dependency and a bundle for five
// constructs.

function inline(text, keyPrefix) {
  // One split on every delimiter so the three can appear in the same line. **bold** is listed before
  // *italic* deliberately: the alternation is ordered, so a leading `**` must be claimed by the bold
  // branch or the italic one would match its first two asterisks and swallow the closing pair.
  // Italic was unsupported until 2026-08-26, which rendered 53 spans of real help copy as literal
  // asterisks -- one regex here rather than rewriting every one of them.
  return text.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*|`[^`]+`)/g).filter(Boolean).map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={key}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('*') && part.endsWith('*')) return <em key={key}>{part.slice(1, -1)}</em>;
    if (part.startsWith('`') && part.endsWith('`')) return <code key={key} className="help-code">{part.slice(1, -1)}</code>;
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
}

export function renderHelpBody(body) {
  const blocks = (body || '').trim().split(/\n\s*\n/);
  return blocks.map((block, bi) => {
    const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines[0]?.startsWith('## ')) {
      return <h3 key={bi} className="help-h">{inline(lines[0].slice(3), `h${bi}`)}</h3>;
    }
    if (lines[0]?.startsWith('- ') || lines[0]?.match(/^\d+\.\s/)) {
      const ordered = !lines[0].startsWith('- ');
      // A wrapped bullet continues the previous item rather than starting a new one.
      const items = [];
      for (const line of lines) {
        const marker = line.match(/^(?:- |\d+\.\s)/);
        if (marker) items.push(line.slice(marker[0].length));
        else if (items.length) items[items.length - 1] += ` ${line}`;
      }
      const List = ordered ? 'ol' : 'ul';
      return (
        <List key={bi} className="help-list">
          {items.map((item, i) => <li key={i}>{inline(item, `${bi}-${i}`)}</li>)}
        </List>
      );
    }
    return <p key={bi} className="help-p">{inline(lines.join(' '), `p${bi}`)}</p>;
  });
}

// ---------- Drawer ----------

function TopicButton({ topic, onSelect, isChild = false }) {
  return (
    <button
      type="button"
      className={`help-topic-btn${isChild ? ' is-child' : ''}`}
      onClick={() => onSelect(topic.id)}
    >
      <span className="help-topic-title">{topic.title}</span>
      <span className="help-topic-summary">{topic.summary}</span>
    </button>
  );
}

/**
 * `focusTopicId` / `onFocusTopicConsumed` are the handoff from Peter: a citation clicked in his
 * floating widget opens this drawer straight to the article it named, instead of at this screen's
 * usual topic. Consumed once so a later plain open of Help doesn't reuse a stale target.
 */
function HelpDrawer({ open, onClose, onAskPeter, focusTopicId, onFocusTopicConsumed }) {
  const { pathname } = useLocation();
  const { currentUser, permissions } = useApp();
  const [query, setQuery] = React.useState('');
  const [selectedId, setSelectedId] = React.useState('');
  const searchRef = React.useRef(null);

  const viewer = React.useMemo(
    () => ({ isAdmin: currentUser?.role === 'admin', permissions, flags: FLAGS }),
    [currentUser, permissions],
  );
  const topics = React.useMemo(() => visibleTopics(viewer, HELP_TOPICS), [viewer]);
  const screenTopic = React.useMemo(() => topicForPath(pathname, topics), [pathname, topics]);

  // Opening Help is a fresh question: land on this screen's article (or Peter's handoff target,
  // when there is one), clear the last search.
  React.useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelectedId(focusTopicId || (screenTopic ? screenTopic.id : ''));
    if (focusTopicId) onFocusTopicConsumed?.();
    // Focus search rather than the article — searching is the other half of what this panel is for.
    const t = setTimeout(() => searchRef.current?.focus(), 50);
    return () => clearTimeout(t);
    // Intentionally keyed on `open` only: re-running on navigation would fight a user mid-read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const results = query.trim() ? searchTopics(topics, query) : null;
  const selected = selectedId ? topicById(selectedId, topics) : null;
  const related = (selected?.related || []).map((id) => topicById(id, topics)).filter(Boolean);
  const selectedChildren = selected ? topics.filter((t) => t.parent === selected.id) : [];
  const screenChildren = screenTopic ? topics.filter((t) => t.parent === screenTopic.id) : [];
  const shownAbove = new Set(screenTopic ? [screenTopic.id, ...screenChildren.map((t) => t.id)] : []);

  return (
    <>
      <div className="help-overlay" onClick={onClose} />
      <aside className="help-drawer" role="dialog" aria-modal="true" aria-label="Help">
        <div className="help-drawer-head">
          <div className="help-drawer-title">Help</div>
          <button type="button" className="help-icon-btn" onClick={onClose} aria-label="Close help">
            <X size={18} strokeWidth={1.75} aria-hidden />
          </button>
        </div>

        <div className="help-search">
          <Search size={15} strokeWidth={1.75} aria-hidden className="help-search-icon" />
          <input
            ref={searchRef}
            className="help-search-input"
            type="search"
            placeholder="Search help…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search help topics"
          />
        </div>

        <div className="help-body">
          {results ? (
            <>
              <div className="help-section-label">
                {results.length} result{results.length === 1 ? '' : 's'} for “{query.trim()}”
              </div>
              {results.length === 0 ? (
                <p className="help-p help-muted">
                  Nothing matched. Try a word from the screen you're on — or clear the search to browse
                  every topic.
                </p>
              ) : (
                results.map((t) => (
                  <TopicButton
                    key={t.id}
                    topic={t}
                    onSelect={(id) => { setSelectedId(id); setQuery(''); }}
                  />
                ))
              )}
            </>
          ) : selected ? (
            <>
              <button type="button" className="help-back" onClick={() => setSelectedId('')}>
                <ChevronLeft size={14} strokeWidth={2} aria-hidden /> All topics
              </button>
              <h2 className="help-article-title">{selected.title}</h2>
              {selected.id === screenTopic?.id && (
                <div className="help-onscreen-tag">Help for this screen</div>
              )}
              <div className="help-article">{renderHelpBody(selected.body)}</div>
              {selectedChildren.length > 0 && (
                <>
                  <div className="help-section-label">More detail</div>
                  {selectedChildren.map((t) => <TopicButton key={t.id} topic={t} onSelect={setSelectedId} />)}
                </>
              )}
              {related.length > 0 && (
                <>
                  <div className="help-section-label">Related</div>
                  {related.map((t) => <TopicButton key={t.id} topic={t} onSelect={setSelectedId} />)}
                </>
              )}
            </>
          ) : (
            <>
              {screenTopic && (
                <>
                  <div className="help-section-label">This screen</div>
                  <TopicButton topic={screenTopic} onSelect={setSelectedId} />
                  {/* The screen's detail topics come with it -- on Resource Planning, the three views
                      are what someone actually opened Help for. */}
                  {screenChildren.map((t) => (
                    <TopicButton key={t.id} topic={t} isChild onSelect={setSelectedId} />
                  ))}
                </>
              )}
              {CATEGORIES.map((category) => {
                const inCategory = orderTopics(topics.filter((t) => t.category === category))
                  .filter((row) => !shownAbove.has(row.topic.id));
                if (inCategory.length === 0) return null;
                return (
                  <React.Fragment key={category}>
                    <div className="help-section-label">{category}</div>
                    {inCategory.map(({ topic, isChild }) => (
                      <TopicButton
                        key={topic.id}
                        topic={topic}
                        isChild={isChild}
                        onSelect={setSelectedId}
                      />
                    ))}
                  </React.Fragment>
                );
              })}
            </>
          )}
        </div>

        {PETER_ENABLED ? (
          <button type="button" className="peter-launch" onClick={onAskPeter}>
            <PeterMark size={26} />
            <span className="peter-launch-copy">
              <strong>Ask Peter</strong>
              <em>Can't find it? Ask in your own words.</em>
            </span>
          </button>
        ) : (
          <div className="help-drawer-foot">
            Can't find it? Ask Jeremy. An assistant — <strong>Peter the Project Monkey</strong> — is
            planned for asking questions like this in your own words.
          </div>
        )}
      </aside>
    </>
  );
}

export default HelpDrawer;
